/*
 * <country-flag-selector>: the flag at the right end of the announcement
 * marquee and its country/region menu (snippets/country-flag-selector.liquid).
 *
 * The menu is a native popover, so opening, closing, Escape, click-outside and
 * returning focus work without this file. This file adds:
 *   - the rows themselves: they ship inside a noscript, which the parser keeps
 *     as one text node while scripting is on, and are parsed here on the first
 *     press of the flag (see the snippet for why)
 *   - flags in the menu, made only while it is open and only for rows in or
 *     near view (each row ships an empty span holding the URL)
 *   - placement under the bar, right-aligned to the flag, sized to the viewport
 *   - search, arrow keys, and aria-expanded on the toggle
 *
 * A browser without the Popover API (Safari < 17) gets no flag at all rather
 * than a button that does nothing: the element hides itself. The footer's
 * country selector still works there.
 *
 * Everything is declared inside the define guard: a section that emits this
 * script tag runs the file once per instance, and a top-level let/const/class
 * throws on the second run.
 */
if (!customElements.get('country-flag-selector')) {
  // Rows whose flags start downloading the moment the menu is pressed, before
  // it has painted: about one screen of the menu at scroll 0 (the popular
  // group plus the first countries). Everything after that waits for the
  // IntersectionObserver.
  const PRIME_COUNT = 10;
  const FINE_POINTER = window.matchMedia('(hover: hover) and (pointer: fine)');

  const normalize = (value) =>
    value
      .normalize('NFD')
      .replace(/\p{Diacritic}/gu, '')
      .toLowerCase()
      .trim();

  const loadFlag = (slot) => {
    const src = slot.getAttribute('data-flag-src');
    if (!src) return;
    slot.removeAttribute('data-flag-src');
    const img = document.createElement('img');
    img.alt = '';
    img.width = 20;
    img.height = 15;
    img.decoding = 'async';
    img.src = src;
    slot.appendChild(img);
  };

  class CountryFlagSelector extends HTMLElement {
    connectedCallback() {
      if (!this.initialized && !this.init()) return;
      window.addEventListener('pagehide', this.onPageHide);
    }

    // Once per element: a moved element reconnects, and binding again would
    // make new function references that addEventListener cannot de-duplicate.
    init() {
      this.toggle = this.querySelector('[data-country-toggle]');
      this.panel = this.querySelector('[data-country-panel]');
      if (!this.toggle || !this.panel) return false;

      if (typeof this.panel.showPopover !== 'function') {
        this.hidden = true;
        return false;
      }

      this.scroller = this.panel.querySelector('[data-country-scroller]');
      this.search = this.panel.querySelector('[data-country-search]');
      this.status = this.panel.querySelector('[data-country-status]');

      this.prime = this.prime.bind(this);
      this.onToggleKeydown = this.onToggleKeydown.bind(this);
      this.onBeforeToggle = this.onBeforeToggle.bind(this);
      this.onToggle = this.onToggle.bind(this);
      this.onKeydown = this.onKeydown.bind(this);
      this.onFocusOut = this.onFocusOut.bind(this);
      this.onSearchInput = this.onSearchInput.bind(this);
      this.onResize = this.onResize.bind(this);
      this.onPageHide = this.onPageHide.bind(this);

      this.toggle.setAttribute('aria-expanded', String(this.isOpen()));
      this.toggle.setAttribute('aria-controls', this.panel.id);
      this.toggle.addEventListener('pointerdown', this.prime, { passive: true });
      this.toggle.addEventListener('keydown', this.onToggleKeydown);
      this.panel.addEventListener('beforetoggle', this.onBeforeToggle);
      this.panel.addEventListener('toggle', this.onToggle);
      this.panel.addEventListener('keydown', this.onKeydown);
      // On the element, not the panel: Shift+Tab off the toggle leaves too.
      this.addEventListener('focusout', this.onFocusOut);

      if (this.search) {
        this.search.closest('[data-country-search-wrapper]').hidden = false;
        this.search.addEventListener('input', this.onSearchInput);
      }

      this.initialized = true;

      // Opened before this deferred script ran (it waits for the whole
      // document to parse): fill the menu in now.
      if (this.isOpen()) {
        this.prime();
        this.place();
        this.toggle.setAttribute('aria-expanded', 'true');
        this.observeFlags();
        this.placedWidth = window.innerWidth;
        window.addEventListener('resize', this.onResize, { passive: true });
      }
      return true;
    }

    disconnectedCallback() {
      if (this.flagObserver) this.flagObserver.disconnect();
      this.flagObserver = null;
      clearTimeout(this.statusTimer);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('pagehide', this.onPageHide);
    }

    isOpen() {
      try {
        return this.panel.matches(':popover-open');
      } catch (error) {
        return false;
      }
    }

    close() {
      if (this.isOpen()) this.panel.hidePopover();
    }

    // On the press itself, before the menu paints: build the rows, and start
    // the first screen of flags so they are usually in by the time it opens.
    prime() {
      if (this.primed) return;
      this.primed = true;
      this.ensureRows();
      const slots = this.panel.querySelectorAll('[data-flag-src]');
      for (let i = 0; i < slots.length && i < PRIME_COUNT; i += 1) loadFlag(slots[i]);
    }

    // With scripting on, the noscript holding the rows is a single text node
    // of their markup. Parse it once, in place. (With scripting off the rows
    // are already elements and this script never runs.)
    ensureRows() {
      const source = this.scroller && this.scroller.querySelector('noscript[data-country-rows]');
      if (source) {
        const parsed = document.createElement('template');
        parsed.innerHTML = source.textContent;
        source.replaceWith(parsed.content);
        this.items = null;
      }
      this.popularGroup = this.panel.querySelector('[data-country-popular]');
      this.emptyMessage = this.panel.querySelector('[data-country-empty]');
    }

    onToggleKeydown(event) {
      if (event.key === 'Enter' || event.key === ' ') this.prime();
    }

    onBeforeToggle(event) {
      if (event.newState !== 'open') return;
      this.prime();
      this.place();
    }

    onToggle(event) {
      const open = event.newState === 'open';
      this.toggle.setAttribute('aria-expanded', String(open));

      if (open) {
        this.observeFlags();
        this.placedWidth = window.innerWidth;
        window.addEventListener('resize', this.onResize, { passive: true });
        // Straight into the search on desktop. Not on touch screens, where
        // focusing a field opens the keyboard over the list.
        if (this.search && FINE_POINTER.matches) this.search.focus({ preventScroll: true });
        return;
      }

      window.removeEventListener('resize', this.onResize);
      if (this.search && this.search.value) {
        this.search.value = '';
        this.filter('');
      }
    }

    // Under the bar, right edge on the flag's right edge. The panel is
    // position: absolute in the top layer, where the containing block is the
    // initial containing block: document coordinates, so it scrolls away with
    // the bar instead of floating over the page.
    place() {
      const anchor = this.closest('[data-country-menu-anchor]') || this;
      const bar = anchor.getBoundingClientRect();
      const toggle = this.toggle.getBoundingClientRect();
      const viewportWidth = document.documentElement.clientWidth;
      const gutter = 8;
      const right = Math.max(viewportWidth - toggle.right, gutter);
      const style = this.panel.style;
      style.setProperty('--country-menu-top', `${Math.round(bar.bottom + window.scrollY)}px`);
      style.setProperty('--country-menu-right', `${Math.round(right - window.scrollX)}px`);
      style.setProperty('--country-menu-max-width', `${Math.round(viewportWidth - right - gutter)}px`);
      style.setProperty(
        '--country-menu-max-height',
        `${Math.round(Math.max(window.innerHeight - Math.max(bar.bottom, 0) - 16, 240))}px`
      );
    }

    // Width only: on iOS the toolbar changes the height on every scroll, and
    // re-placing on each of those would make the open menu jump.
    onResize() {
      if (window.innerWidth === this.placedWidth) return;
      this.placedWidth = window.innerWidth;
      this.place();
    }

    observeFlags() {
      const slots = this.panel.querySelectorAll('[data-flag-src]');
      if (!slots.length) return;
      if (!('IntersectionObserver' in window)) {
        slots.forEach(loadFlag);
        return;
      }
      if (!this.flagObserver) {
        this.flagObserver = new IntersectionObserver(
          (entries, observer) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              observer.unobserve(entry.target);
              loadFlag(entry.target);
            });
          },
          // A little over half a menu-height ahead in both directions, so
          // flags are usually in before a scroll brings their rows up.
          { root: this.scroller, rootMargin: '60% 0px' }
        );
      }
      slots.forEach((slot) => this.flagObserver.observe(slot));
    }

    onSearchInput() {
      this.filter(this.search.value);
    }

    filter(value) {
      const query = normalize(value);
      if (!this.items) {
        this.items = Array.from(this.panel.querySelectorAll('[data-country-all] > li'), (li) => {
          const option = li.querySelector('.country-flag-selector__option');
          const name = li.querySelector('.country-flag-selector__option-name');
          return {
            li,
            name: normalize(name ? name.textContent : ''),
            code: option ? option.value.toLowerCase() : '',
          };
        });
      }

      let count = 0;
      this.items.forEach((item) => {
        const match = !query || item.name.includes(query) || item.code === query;
        item.li.hidden = !match;
        if (match) count += 1;
      });

      if (this.popularGroup) this.popularGroup.hidden = query !== '';
      if (this.emptyMessage) this.emptyMessage.hidden = count > 0;
      if (this.scroller) this.scroller.scrollTop = 0;
      this.announce(query ? count : null);
    }

    // Debounced so a screen reader hears the count once typing pauses, not
    // once per keystroke.
    announce(count) {
      clearTimeout(this.statusTimer);
      if (!this.status) return;
      if (count === null) {
        this.status.textContent = '';
        return;
      }
      this.statusTimer = setTimeout(() => {
        this.status.textContent = (this.status.dataset.template || '').replace('[count]', count);
      }, 500);
    }

    visibleOptions() {
      return Array.from(this.panel.querySelectorAll('.country-flag-selector__option')).filter(
        (option) => !option.closest('[hidden]')
      );
    }

    onKeydown(event) {
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const inSearch = event.target === this.search;

      // Enter in the search box would submit the form with its first button,
      // which is a country the shopper never chose. Move to the first match.
      if (inSearch && event.key === 'Enter') {
        event.preventDefault();
        const first = this.visibleOptions()[0];
        if (first) first.focus();
        return;
      }

      if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
      const options = this.visibleOptions();
      if (!options.length) return;

      // In the field, Up/Home/End keep their text-editing meaning.
      if (inSearch) {
        if (event.key !== 'ArrowDown') return;
        event.preventDefault();
        options[0].focus();
        return;
      }

      const index = options.indexOf(event.target);
      if (index === -1) return;
      event.preventDefault();

      if (event.key === 'ArrowUp' && index === 0 && this.search && !this.search.closest('[hidden]')) {
        this.search.focus();
        return;
      }

      const last = options.length - 1;
      const next = {
        ArrowDown: Math.min(index + 1, last),
        ArrowUp: Math.max(index - 1, 0),
        Home: 0,
        End: last,
      }[event.key];
      options[next].focus();
    }

    // Focus leaving the flag and its menu closes the menu (the panel is a DOM
    // descendant here even while it paints in the top layer). A null
    // relatedTarget is ignored: a mouse click in Safari moves focus to
    // nothing, and closing then would swallow the click on the country the
    // shopper picked.
    onFocusOut(event) {
      const next = event.relatedTarget;
      if (!next || this.contains(next)) return;
      this.close();
    }

    // Leaving for another country: close the menu so Back (bfcache) does not
    // return to a page with it still open.
    onPageHide() {
      this.close();
    }
  }

  customElements.define('country-flag-selector', CountryFlagSelector);
}
